import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usersApi } from "../../api/users.api";
import { appsApi } from "../../api/apps.api";
import { useAuthStore } from "../../store/auth.store";

function describeError(err: unknown): string {
  if (err && typeof err === "object" && "response" in err) {
    const r = (err as { response?: { data?: { error?: { message?: string }; message?: string } } }).response;
    if (r?.data?.error?.message) return r.data.error.message;
    if (r?.data?.message) return r.data.message;
  }
  return "Something went wrong";
}

export function AdminUserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const startImpersonating = useAuthStore((s) => s.startImpersonating);

  const userQ = useQuery({
    queryKey: ["users", userId],
    queryFn: () => usersApi.get(userId!),
    enabled: !!userId,
  });
  const appsQ = useQuery({ queryKey: ["apps"], queryFn: appsApi.list });

  const update = useMutation({
    mutationFn: (input: Parameters<typeof usersApi.update>[1]) =>
      usersApi.update(userId!, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users", userId] });
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const setApps = useMutation({
    mutationFn: (appIds: string[]) => usersApi.setApps(userId!, appIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users", userId] });
    },
  });

  const remove = useMutation({
    mutationFn: () => usersApi.remove(userId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      navigate("/admin/users");
    },
  });

  const sendInvite = useMutation({
    mutationFn: () => usersApi.sendInvite(userId!),
  });

  const setBackupCode = useMutation({
    mutationFn: (code: string | null) => usersApi.setBackupCode(userId!, code),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users", userId] });
    },
  });

  const impersonate = useMutation({
    mutationFn: () => usersApi.impersonate(userId!),
    onSuccess: (data) => {
      startImpersonating({ accessToken: data.accessToken, user: data.user });
      qc.clear();
      navigate("/");
    },
  });

  const [grantedAppIds, setGrantedAppIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (userQ.data) {
      setGrantedAppIds(new Set(userQ.data.appAccess.map((a) => a.id)));
    }
  }, [userQ.data]);

  const accessChanged = useMemo(() => {
    if (!userQ.data) return false;
    const current = new Set(userQ.data.appAccess.map((a) => a.id));
    if (current.size !== grantedAppIds.size) return true;
    for (const id of grantedAppIds) if (!current.has(id)) return true;
    return false;
  }, [userQ.data, grantedAppIds]);

  if (userQ.isLoading) return <p className="muted">Loading…</p>;
  if (userQ.error || !userQ.data) return <p className="error-text">User not found</p>;

  const u = userQ.data;
  const isSelf = me?.id === u.id;

  function toggleApp(appId: string) {
    setGrantedAppIds((prev) => {
      const next = new Set(prev);
      if (next.has(appId)) next.delete(appId);
      else next.add(appId);
      return next;
    });
  }

  function saveAccess() {
    setApps.mutate(Array.from(grantedAppIds));
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h2>{u.displayName ?? u.email}</h2>
          <div className="muted">{u.email}</div>
        </div>
        <Link to="/admin/users">← All users</Link>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Account</h3>
        <dl className="detail-grid">
          <dt className="muted">Auth</dt>
          <dd>
            {u.hasEntra ? (
              <span className="badge muted">Microsoft Entra</span>
            ) : (
              <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span className="badge warning">No Entra link — user must sign in first</span>
                <CopySignInLink />
              </span>
            )}
          </dd>
          <dt className="muted">Last login</dt>
          <dd>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : "—"}</dd>
          <dt className="muted">Status</dt>
          <dd>{u.disabled ? <span className="badge danger">Disabled</span> : <span className="badge success">Active</span>}</dd>
        </dl>

        <div style={{ display: "grid", gap: 8, maxWidth: 400 }}>
          <Toggle
            label="Panel admin"
            checked={u.isPanelAdmin}
            disabled={isSelf}
            help={isSelf ? "You can't revoke your own admin access" : undefined}
            onChange={(v) => update.mutate({ isPanelAdmin: v })}
          />
          <Toggle
            label="Disabled"
            checked={u.disabled}
            disabled={isSelf}
            onChange={(v) => update.mutate({ disabled: v })}
          />
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
          <button
            onClick={() => sendInvite.mutate()}
            disabled={sendInvite.isPending || sendInvite.isSuccess}
          >
            {sendInvite.isPending ? "Sending…" : sendInvite.isSuccess ? "Invite sent ✓" : "Send invite email"}
          </button>
          {!isSelf && !u.isPanelAdmin && !u.disabled && (
            <button
              onClick={() => {
                if (confirm(`Sign in as ${u.email}? You'll see exactly what they see. Use the banner to stop.`)) {
                  impersonate.mutate();
                }
              }}
              disabled={impersonate.isPending}
            >
              {impersonate.isPending ? "Switching…" : "Impersonate"}
            </button>
          )}
          {!isSelf && (
            <button
              className="danger"
              onClick={() => {
                if (confirm(`Delete user ${u.email}? This cannot be undone.`)) remove.mutate();
              }}
              disabled={remove.isPending}
            >
              Delete user
            </button>
          )}
        </div>
        {sendInvite.isError && (
          <p className="error-text">{describeError(sendInvite.error)}</p>
        )}
        {impersonate.isError && (
          <p className="error-text">{describeError(impersonate.error)}</p>
        )}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>PmFlow profile</h3>
        {u.paycomId ? (
          <dl className="detail-grid">
            <dt className="muted">Paycom employee ID</dt>
            <dd>{u.paycomId}</dd>
            {u.jobTitle && (
              <>
                <dt className="muted">Job title</dt>
                <dd>{u.jobTitle}</dd>
              </>
            )}
            {u.department && (
              <>
                <dt className="muted">Trade / department</dt>
                <dd>{u.department}</dd>
              </>
            )}
            <dt className="muted">Mobile phone</dt>
            <dd>
              {u.mobilePhone ? (
                <span>
                  {u.mobilePhone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")}
                  <span
                    className="badge muted"
                    style={{ marginLeft: 8 }}
                    title="Used as the send-to address for SMS magic-link and code confirmations"
                  >
                    SMS auth
                  </span>
                </span>
              ) : (
                <span className="badge warning">Not set — SMS auth unavailable</span>
              )}
            </dd>
            {u.paycomSyncedAt && (
              <>
                <dt className="muted">Last synced from Paycom</dt>
                <dd className="muted">{new Date(u.paycomSyncedAt).toLocaleString()}</dd>
              </>
            )}
          </dl>
        ) : (
          <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
            This user has not been linked to a Paycom employee record. Run{" "}
            <strong>Sync from Paycom</strong> on the Users page to import their mobile phone,
            job title, and department.
          </p>
        )}

        <hr style={{ border: "none", borderTop: "1px solid var(--border, #e5e5e5)", margin: "16px 0" }} />

        <BackupCodeEditor
          current={u.backupCode}
          onSave={(v) => setBackupCode.mutate(v)}
          isPending={setBackupCode.isPending}
          error={setBackupCode.isError ? describeError(setBackupCode.error) : null}
        />
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>App access</h3>
        <p className="muted" style={{ fontSize: 13 }}>
          Apps the user can launch from their dashboard.
        </p>
        {appsQ.data && appsQ.data.length === 0 && (
          <p className="muted">No apps registered yet.</p>
        )}
        {appsQ.data && appsQ.data.length > 0 && (
          <div style={{ display: "grid", gap: 6, marginBottom: 12 }}>
            {appsQ.data.map((app) => (
              <label key={app.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  style={{ width: "auto" }}
                  checked={grantedAppIds.has(app.id)}
                  onChange={() => toggleApp(app.id)}
                />
                <span>{app.name}</span>
                <span className="badge muted">{app.environment}</span>
              </label>
            ))}
          </div>
        )}
        <button
          className="primary"
          onClick={saveAccess}
          disabled={!accessChanged || setApps.isPending}
        >
          {setApps.isPending ? "Saving…" : "Save app access"}
        </button>
      </div>
    </>
  );
}

function CopySignInLink() {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/api/v1/auth/entra/login`;
  function copy() {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <button onClick={copy} style={{ fontSize: 12, padding: "2px 8px" }}>
      {copied ? "Copied ✓" : "Copy sign-in link"}
    </button>
  );
}

interface BackupCodeEditorProps {
  current: string | null;
  onSave: (value: string | null) => void;
  isPending: boolean;
  error: string | null;
}

function BackupCodeEditor({ current, onSave, isPending, error }: BackupCodeEditorProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");

  function start() {
    setValue("");
    setEditing(true);
  }

  function save() {
    onSave(value);
    setEditing(false);
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <strong style={{ fontSize: 14 }}>Backup code</strong>
        <span
          className="badge muted"
          title="4-digit backup login code for PmFlow, confirmed by SMS (MFA) to the mobile number"
        >
          PmFlow login
        </span>
      </div>
      <p className="muted" style={{ fontSize: 12, margin: "0 0 8px" }}>
        Any 4-digit code (e.g. SSN last-4). Entered manually and stored encrypted. Used as the
        PmFlow backup login code, with MFA confirmed by SMS to the mobile number above.
      </p>

      {!editing ? (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {current ? (
            <span style={{ fontFamily: "monospace", letterSpacing: 2 }}>••••</span>
          ) : (
            <span className="badge warning">Not set — backup code login unavailable</span>
          )}
          <button style={{ fontSize: 12, padding: "2px 10px" }} onClick={start}>
            {current ? "Change" : "Set backup code"}
          </button>
          {current && (
            <button
              className="danger"
              style={{ fontSize: 12, padding: "2px 10px" }}
              disabled={isPending}
              onClick={() => onSave(null)}
            >
              Clear
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            inputMode="numeric"
            maxLength={4}
            placeholder="1234"
            value={value}
            onChange={(e) => setValue(e.target.value.replace(/\D/g, "").slice(0, 4))}
            style={{ width: 80, fontFamily: "monospace", letterSpacing: 2 }}
            autoFocus
          />
          <button
            className="primary"
            style={{ fontSize: 12, padding: "2px 10px" }}
            disabled={value.length !== 4 || isPending}
            onClick={save}
          >
            {isPending ? "Saving…" : "Save"}
          </button>
          <button
            style={{ fontSize: 12, padding: "2px 10px" }}
            onClick={() => setEditing(false)}
          >
            Cancel
          </button>
        </div>
      )}
      {error && <p className="error-text" style={{ fontSize: 12, marginTop: 6 }}>{error}</p>}
    </div>
  );
}

interface ToggleProps {
  label: string;
  checked: boolean;
  disabled?: boolean;
  help?: string;
  onChange: (v: boolean) => void;
}

function Toggle({ label, checked, disabled, help, onChange }: ToggleProps) {
  return (
    <label
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <span>
        {label}
        {help && <div className="muted" style={{ fontSize: 12 }}>{help}</div>}
      </span>
      <input
        type="checkbox"
        style={{ width: "auto" }}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}
